import { hasPartnerPortalAccess, useAuth } from "@/context/auth";
import { usePermissions } from "@/context/permissions";

// Mapeamento de rotas e suas permissões necessárias.
// Usado pelo useFirstAllowedRoute para escolher a primeira área que o
// usuário pode acessar quando ele cair em uma rota sem permissão.
export const routePermissions: Array<{
  path: string;
  permission: string;
}> = [
  {
    path: "/clients",
    permission: "partner:self:view",
  },
  { path: "/dashboard", permission: "dashboard:view" },
  { path: "/reports", permission: "reports:view" },
  { path: "/insurance-companies", permission: "insurance:view" },
  { path: "/products", permission: "products:view" },
  { path: "/clients", permission: "partners:view" },
  { path: "/users", permission: "user:view" },
  { path: "/whatsapp-meta", permission: "whatsapp:view" },
  { path: "/invoices", permission: "invoices:view" },
  { path: "/settings", permission: "settings:view" },
];

/**
 * Hook para encontrar a primeira página permitida para o usuário
 * Prioriza o dashboard se o usuário tiver permissão
 */
export const useFirstAllowedRoute = (): string | null => {
  const { hasPermission, isLoading } = usePermissions();
  const { auth } = useAuth();

  if (isLoading) {
    return null;
  }

  if (
    hasPartnerPortalAccess(auth.roles) &&
    auth.organization_root_id
  ) {
    return "/clients";
  }

  // Prioriza o dashboard se o usuário tiver permissão
  if (hasPermission("dashboard:view")) {
    return "/dashboard";
  }

  // Caso contrário, tenta encontrar a primeira rota permitida
  for (const route of routePermissions) {
    if (hasPermission(route.permission)) {
      return route.path;
    }
  }

  // Se não encontrou nenhuma, retorna null
  return null;
};

/**
 * Função utilitária para encontrar a primeira página permitida
 * (para uso fora de componentes React)
 * Prioriza o dashboard se o usuário tiver permissão
 */
export const getFirstAllowedRoute = (
  hasPermission: (permission: string) => boolean
): string | null => {
  // Prioriza o dashboard se o usuário tiver permissão
  if (hasPermission("dashboard:view")) {
    return "/dashboard";
  }

  // Caso contrário, tenta encontrar a primeira rota permitida
  for (const route of routePermissions) {
    if (hasPermission(route.permission)) {
      return route.path;
    }
  }
  return null;
};

/**
 * Função para obter a primeira página permitida baseada nas permissões do usuário
 * Usa o cookie para obter as permissões (para uso em redirecionamentos)
 */
export const getFirstAllowedRouteFromPermissions = (
  permissions: Array<{ code: string }>
): string | null => {
  const hasPermission = (code: string): boolean => {
    return permissions.some((perm) => perm.code === code);
  };

  return getFirstAllowedRoute(hasPermission);
};

